// The modern, three-column commit graph at the CENTER of the immersive Git
// client (design 2.3). It evolves the sidebar HistoryView into a wider layout:
//
//   Branch / Tag  |        Graph        |  Commit Message
//   ref pills      author-avatar rails     subject + author/date/hash
//
// Like HistoryView it lays out lanes client-side with `layoutCommits` and draws
// per-row rails as inline SVG, but the commit node is an author CommitAvatar and
// each row carries a subtle tint in its lane color. A branch <select> narrows the
// log to one ref, "Load more" pages, and the commit context menu (branch / tag /
// detached checkout / copy hash) is preserved. Rows are selectable: clicking a
// commit reports `{ kind: 'commit', hash }`, and a synthetic "Uncommitted
// changes" node pinned at the top (when the working tree is dirty) reports
// 'working' so the right panel can show staging. All `git` access is guarded
// because the capability is optional (desktop-only).
//
// DEFERRED to slice 7b (NOT built here): the collapsing-rails horizontal scroll,
// the isolate-ancestry dimming, and the searchable branch combobox. This slice
// keeps a plain <select> filter and normal vertical scrolling.

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import type { GitApi, GitBranches, GitCommit } from '@slate/core';
import {
  Check,
  ChevronsLeftRight,
  ChevronsRightLeft,
  Copy,
  Focus,
  GitBranch,
  GitBranchPlus,
  RefreshCw,
  Tag,
  X,
} from 'lucide-react';
import { useContextMenu, type ContextMenuEntry } from '../../context-menu';
import { CommitAvatar } from '../CommitAvatar';
import { CommitAuthorHover } from '../CommitAuthorHover';
import { GitBranchCombobox } from './GitBranchCombobox';
import { GitNamePrompt } from '../GitNamePrompt';
import { relativeTime } from '../relativeTime';
import { ancestorsOf, layoutCommits, type GraphRow } from '../graphLayout';

/** What the center graph currently has selected, driving the right commit panel. */
export type GitSelection = 'working' | { kind: 'commit'; hash: string } | null;

export interface GitGraphViewProps {
  /** The repo to read history from, or null when no repo resolves. */
  root: string | null;
  /** The platform GitApi (guarded; undefined when unavailable). */
  git: GitApi | undefined;
  /** Bumped by the client to force a re-read after a cross-pane mutation. */
  refreshToken: number;
  /** The current selection (a commit, the working tree, or nothing). */
  selection: GitSelection;
  /** Report a new selection (commit row click, or the uncommitted node). */
  onSelect(sel: GitSelection): void;
  /** Notify the client a mutation happened so the other panes refresh. */
  onMutated(): void;
  /** When true, pin a synthetic "Uncommitted changes" node at the top. */
  workingDirty: boolean;
  /**
   * Header-toolbar search query. When non-empty, only commit rows whose subject,
   * author, or hash (short or full, case-insensitive) contain the query are
   * rendered. Lanes are still laid out over the FULL list so the visible rows
   * keep their correct lane colors; the synthetic "Uncommitted changes" node
   * always shows regardless of the query.
   */
  search?: string;
  /**
   * Controlled branch-scope filter, lifted to the client so the left-nav ref
   * list and this view's own branch <select> share one source of truth. When
   * `onRevChange` is provided this view is CONTROLLED (it uses `rev` and reports
   * changes upward); when omitted it keeps its own internal filter state.
   */
  rev?: string;
  onRevChange?: (rev: string | undefined) => void;
  /**
   * Reveal request from the left nav: when `revealToken` changes, scroll the
   * commit `revealHash` into view and select it (a ref "jump to", not a filter
   * or checkout).
   */
  revealHash?: string;
  revealToken?: number;
  /**
   * Isolate/focus: dim every commit outside this commit's ancestry. Controlled
   * when `onIsolate` is provided (so the left nav can focus a ref); otherwise the
   * view keeps its own isolate state (right-click a commit). Escape clears it.
   */
  isolate?: string | null;
  onIsolate?: (hash: string | null) => void;
}

/** An active inline name prompt seeded from a commit (branch or tag creation). */
interface CommitPrompt {
  kind: 'branch' | 'tag';
  commit: GitCommit;
}

/** Commits fetched per page (also the threshold that surfaces "Load more"). */
const PAGE = 500;
/** Horizontal spacing between graph lanes, in px. Keep in sync with the CSS. */
const LANE_W = 14;
/** Row height, in px. Mirrors the `--mp-git-row-h` CSS var. */
const ROW_H = 28;
/** Left inset for the rails so the avatar (20px) never clips the cell edge. */
const RAIL_PAD = 12;
/** Avatar diameter for a commit node, in px. */
const AVATAR = 20;
/** Extra rows rendered above/below the viewport so fast scrolls stay filled. */
const OVERSCAN = 8;
/** Minimum window size, so the initial paint (and headless tests, where the
 *  viewport reports zero height) still render a useful slice. */
const MIN_WINDOW = 40;

/**
 * Lane palette indexed by `color % LANE_COLORS.length`. Mirrors HistoryView's
 * palette (kept local so this view does not depend on that component's
 * internals); vivid hues that stay legible on the light and dark surfaces.
 */
const LANE_COLORS = [
  '#4f9cf9', // blue
  '#34c759', // green
  '#ff9f0a', // orange
  '#ff375f', // pink
  '#bf5af2', // purple
  '#30c0c6', // teal
  '#ffd60a', // yellow
  '#64d2ff', // sky
];

function laneColor(color: number): string {
  return LANE_COLORS[((color % LANE_COLORS.length) + LANE_COLORS.length) % LANE_COLORS.length];
}

interface RefPill {
  label: string;
  kind: 'head' | 'tag' | 'branch';
}

/** Pill display priority: the checked-out branch, then tags, then other branches. */
const PILL_RANK: Record<RefPill['kind'], number> = { head: 0, tag: 1, branch: 2 };
/** How many ref pills to show inline before collapsing the rest into a "+N". */
const MAX_PILLS = 3;

/** Classify one `%D` ref decoration into a pill (label + style variant). */
function classifyRef(ref: string, currentBranch: string | null): RefPill {
  if (ref.startsWith('tag: ')) return { label: ref.slice(5), kind: 'tag' };
  if (ref === 'HEAD') return { label: 'HEAD', kind: 'head' };
  // git may decorate the checked-out branch as "HEAD -> main".
  const arrow = ref.indexOf(' -> ');
  const name = arrow >= 0 ? ref.slice(arrow + 4) : ref;
  const isCurrent = arrow >= 0 || (currentBranch != null && name === currentBranch);
  return { label: name, kind: isCurrent ? 'head' : 'branch' };
}

/** True when `selection` is a commit selection matching `hash`. */
function isCommitSelected(selection: GitSelection, hash: string): boolean {
  return selection != null && selection !== 'working' && selection.hash === hash;
}

/**
 * True when a commit matches the header search query: its subject, author, or
 * hash (short or full) contains `needle` (already lowercased) case-insensitively.
 */
function commitMatches(commit: GitCommit, needle: string): boolean {
  return (
    commit.subject.toLowerCase().includes(needle) ||
    commit.author.toLowerCase().includes(needle) ||
    commit.hash.toLowerCase().includes(needle) ||
    commit.short.toLowerCase().includes(needle)
  );
}

interface GraphRowViewProps {
  row: GraphRow;
  currentBranch: string | null;
  selected: boolean;
  dimmed: boolean;
  onSelect: (sel: GitSelection) => void;
  onContextMenu: (e: MouseEvent, commit: GitCommit) => void;
  onPillContextMenu: (e: MouseEvent, pill: RefPill) => void;
}

const GraphRowView = memo(function GraphRowView({
  row,
  currentBranch,
  selected,
  dimmed,
  onSelect,
  onContextMenu,
  onPillContextMenu,
}: GraphRowViewProps): ReactElement {
  const { commit } = row;
  const railW = row.width * LANE_W;
  const dotX = row.lane * LANE_W + LANE_W / 2;
  // Order pills by importance, show the top few, collapse the rest into a "+N".
  const pills = commit.refs
    .map((r) => classifyRef(r, currentBranch))
    .sort((a, b) => PILL_RANK[a.kind] - PILL_RANK[b.kind]);
  const shownPills = pills.slice(0, MAX_PILLS);
  const extraPills = pills.slice(MAX_PILLS);
  const absoluteDate = new Date(commit.date * 1000).toLocaleString();
  const lane = laneColor(row.color);

  const select = (): void => onSelect({ kind: 'commit', hash: commit.hash });
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select();
    }
  };

  return (
    <div
      className="mp-git-graph__row"
      role="option"
      aria-selected={selected}
      data-selected={selected || undefined}
      data-dimmed={dimmed || undefined}
      data-hash={commit.hash}
      tabIndex={0}
      style={{ '--mp-git-lane': lane, '--mp-git-dotx': `${dotX}px` } as CSSProperties}
      onClick={select}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => onContextMenu(e, commit)}
    >
      <div className="mp-git-graph__sticky">
        <span className="mp-git-graph__reftag">
          {shownPills.map((p, i) => (
            <span
              key={`${p.kind}:${p.label}:${i}`}
              className="mp-git-graph__pill"
              data-kind={p.kind}
              title={p.label}
              onContextMenu={(e) => onPillContextMenu(e, p)}
            >
              {p.kind === 'head' && (
                <Check size={11} aria-hidden className="mp-git-graph__pill-check" />
              )}
              <span className="mp-git-graph__pill-label">{p.label}</span>
            </span>
          ))}
          {extraPills.length > 0 && (
            <span
              className="mp-git-graph__pill mp-git-graph__pill--more"
              title={extraPills.map((p) => p.label).join(', ')}
            >
              +{extraPills.length}
            </span>
          )}
        </span>

        <span className="mp-git-graph__rail-cell">
          <svg
            className="mp-git-graph__rail"
            width={railW}
            height={ROW_H}
            viewBox={`0 0 ${railW} ${ROW_H}`}
            aria-hidden
            style={{ left: RAIL_PAD }}
          >
            {pills.length > 0 && (
              <line
                x1={-(RAIL_PAD + 8)}
                y1={ROW_H / 2}
                x2={dotX}
                y2={ROW_H / 2}
                stroke={lane}
                strokeWidth={1.5}
              />
            )}
            {row.edges.map((e, i) => {
              const x1 = e.fromLane * LANE_W + LANE_W / 2;
              const x2 = e.toLane * LANE_W + LANE_W / 2;
              const mid = ROW_H / 2;
              return (
                <path
                  key={i}
                  d={`M${x1} 0 C ${x1} ${mid} ${x2} ${mid} ${x2} ${ROW_H}`}
                  stroke={laneColor(e.color)}
                  strokeWidth={1.5}
                  fill="none"
                />
              );
            })}
          </svg>
          <span className="mp-git-graph__node">
            <CommitAuthorHover
              name={commit.author}
              email={commit.email}
              date={commit.date}
              short={commit.short}
            >
              <CommitAvatar name={commit.author} email={commit.email} size={AVATAR} />
            </CommitAuthorHover>
          </span>
        </span>
      </div>

      <span className="mp-git-graph__msg">
        <span className="mp-git-graph__subject" title={commit.subject}>
          {commit.subject}
        </span>
        <span className="mp-git-graph__metaline">
          <span
            className="mp-git-graph__author"
            title={`${commit.author} <${commit.email}>`}
          >
            {commit.author}
          </span>
          <time
            className="mp-git-graph__date"
            dateTime={new Date(commit.date * 1000).toISOString()}
            title={absoluteDate}
          >
            {relativeTime(commit.date)}
          </time>
          <code className="mp-git-graph__hash">{commit.short}</code>
        </span>
      </span>
    </div>
  );
});

export function GitGraphView({
  root,
  git,
  refreshToken,
  selection,
  onSelect,
  onMutated,
  workingDirty,
  search = '',
  rev: revProp,
  onRevChange,
  revealHash,
  revealToken,
  isolate: isolateProp,
  onIsolate,
}: GitGraphViewProps): ReactElement {
  const [commits, setCommits] = useState<GitCommit[]>([]);
  const [branches, setBranches] = useState<GitBranches | null>(null);
  // The branch-scope filter is CONTROLLED when the client passes `onRevChange`
  // (so the left nav and this <select> stay in sync); otherwise it is internal.
  const controlledRev = onRevChange !== undefined;
  const [internalRev, setInternalRev] = useState<string | undefined>(undefined);
  const rev = controlledRev ? revProp : internalRev;
  const setRev = useCallback(
    (next: string | undefined): void => {
      if (controlledRev) onRevChange(next);
      else setInternalRev(next);
    },
    [controlledRev, onRevChange],
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState(false);
  // The single active inline name prompt (branch or tag), or null when none.
  const [prompt, setPrompt] = useState<CommitPrompt | null>(null);
  const { openContextMenu } = useContextMenu();
  // Whether the most recent page returned exactly PAGE rows (so more may exist).
  const [lastFull, setLastFull] = useState(false);
  // "Isolate/focus": when set to a commit hash, every row outside that commit's
  // reachable ancestry is dimmed so a single line of history stands out.
  // Controlled by the client (so the left nav can focus a ref) when `onIsolate`
  // is provided; otherwise the view keeps its own state (right-click a commit).
  const isolateControlled = onIsolate !== undefined;
  const [internalIsolate, setInternalIsolate] = useState<string | null>(null);
  const isolated = isolateControlled ? (isolateProp ?? null) : internalIsolate;
  const setIsolated = useCallback(
    (next: string | null): void => {
      if (isolateControlled) onIsolate(next);
      else setInternalIsolate(next);
    },
    [isolateControlled, onIsolate],
  );

  // Reset the branch filter when the repo changes. Adjusting state during render
  // (the documented React pattern) re-renders immediately, so the page-0 effect
  // runs once with the cleared filter instead of double-fetching. Only the
  // internal (uncontrolled) filter resets here; when controlled, the client
  // clears its own filterRev on repo change.
  const prevRoot = useRef(root);
  if (prevRoot.current !== root) {
    prevRoot.current = root;
    if (!controlledRev && internalRev !== undefined) setInternalRev(undefined);
  }

  // Refs let an in-flight "Load more" verify the query has not changed before it
  // appends, so a filter switch mid-page cannot stitch the wrong commits on.
  const rootRef = useRef(root);
  rootRef.current = root;
  const revRef = useRef(rev);
  revRef.current = rev;

  // Clear any (uncontrolled) isolate when the repo or branch filter changes: the
  // commit set is different, so the previously isolated tip may not even be
  // loaded anymore. When controlled, the client owns this reset.
  useEffect(() => {
    if (!isolateControlled) setInternalIsolate(null);
  }, [root, rev, isolateControlled]);

  // Escape clears an active isolate/focus from anywhere in the app.
  useEffect(() => {
    if (!isolated) return;
    const onKey = (e: Event): void => {
      if ((e as unknown as { key?: string }).key === 'Escape') setIsolated(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isolated, setIsolated]);

  // Collapsing graph: messages scroll horizontally at full length (natural
  // reading), the avatar rail stays sticky-left, and once the branch column has
  // scrolled past the avatar area the graph SNAPS collapsed (branch folds to
  // nothing, lanes fold to a single line) so the messages get the full width.
  // The snap is a boolean; the morph itself is a smooth CSS transition on the
  // `--mp-git-collapse` custom property (0 <-> 1). Hysteresis (collapse past 64,
  // expand only back at the start) avoids oscillation, since collapsing itself
  // narrows the scrollable width. The toolbar button toggles the same state.
  // One collapse: folds BOTH the Branch/Tag column and the graph lanes into a
  // single avatar rail, handing the freed width to the messages (a smooth CSS
  // transition on the `--mp-git-*collapse` custom properties). It is a binary
  // state driven either by the toolbar button (which also snaps the scroll to the
  // left edge) or by the axis-locked wheel handler below.
  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(false);
  collapsedRef.current = collapsed;
  const listRef = useRef<HTMLDivElement>(null);
  const rafId = useRef(0);
  useEffect(
    () => () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    },
    [],
  );

  // The button toggle snaps the messages to the left edge (deliberate). Scroll-
  // triggered collapse does NOT reset scrollLeft: resetting mid-scroll briefly
  // shows the branch column at the left before it folds, which reads as a glitch.
  const toggleCollapsed = useCallback((): void => {
    setCollapsed((c) => !c);
    if (listRef.current) listRef.current.scrollLeft = 0;
  }, []);

  // Axis-locked wheel scrolling. A gesture commits to ONE axis (whichever
  // dominates first) until it pauses, removing diagonal drift; scrolling is
  // applied manually and clamped so macOS never rubber-bands. Collapse happens
  // only after the messages have scrolled PAST the branch area AND the user keeps
  // deliberately pushing right (an accumulator past a threshold), so a momentum
  // coast never trips it; expanding takes the same deliberate push left from the
  // start. Any reversal resets the accumulator.
  const axisLock = useRef<'x' | 'y' | null>(null);
  const lockTimer = useRef(0);
  const overScroll = useRef(0);
  const COLLAPSE_PUSH = 120;
  // The branch column width (matches --mp-git-branch-w-full): once scrolled this
  // far, the branch area is behind us and a further push collapses.
  const BRANCH_W = 200;
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent): void => {
      const unit = e.deltaMode === 1 ? 16 : 1;
      const dx = e.deltaX * unit;
      const dy = e.deltaY * unit;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
      // Lock to the dominant axis, but let the OTHER axis take over when it
      // clearly dominates (2x) so a vertical scroll right after a horizontal
      // collapse is not swallowed by a stale lock.
      if (axisLock.current === null) axisLock.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      else if (axisLock.current === 'x' && Math.abs(dy) > Math.abs(dx) * 2 + 2) axisLock.current = 'y';
      else if (axisLock.current === 'y' && Math.abs(dx) > Math.abs(dy) * 2 + 2) axisLock.current = 'x';
      if (lockTimer.current) clearTimeout(lockTimer.current);
      lockTimer.current = window.setTimeout(() => {
        axisLock.current = null;
      }, 120);
      e.preventDefault();
      if (axisLock.current === 'y') {
        el.scrollTop = Math.max(0, Math.min(el.scrollTop + dy, el.scrollHeight - el.clientHeight));
        overScroll.current = 0;
        return;
      }
      const maxX = el.scrollWidth - el.clientWidth;
      const x = Math.max(0, Math.min(el.scrollLeft + dx, maxX));
      el.scrollLeft = x;
      if (!collapsedRef.current) {
        // As soon as the branch column has scrolled past, collapse. The branch
        // folds INSTANTLY, so compensate scrollLeft by its width and the messages
        // stay exactly where they are (no jump, no branch flash); the lanes then
        // animate closed.
        if (x > BRANCH_W) {
          overScroll.current = 0;
          setCollapsed(true);
          el.scrollLeft = Math.max(0, x - BRANCH_W);
        }
      } else {
        // Deliberate push left at the very start expands and reveals the branch
        // again from the left edge.
        if (x <= 1 && dx < 0) {
          overScroll.current += dx;
          if (overScroll.current < -COLLAPSE_PUSH) {
            overScroll.current = 0;
            setCollapsed(false);
            el.scrollLeft = 0;
          }
        } else if (dx > 0) {
          overScroll.current = 0;
        }
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (lockTimer.current) clearTimeout(lockTimer.current);
    };
  }, []);

  // Pending reveal target: a commit hash we must scroll to, fetching more pages
  // until it loads. Set on a reveal request; cleared once reached or exhausted.
  const pendingReveal = useRef<string | null>(null);

  // Infinite scroll: auto-fetch the next page as the list nears the bottom, but
  // pause every 5 auto-loaded pages behind a manual "Load more" so history never
  // runs away unbounded. `autoStreak` counts auto pages since the last manual load.
  const autoStreak = useRef(0);
  const [autoPaused, setAutoPaused] = useState(false);

  // --- Windowed (virtualized) rendering: only the rows near the viewport are in
  // the DOM; spacers above/below reserve the full scroll height. Rows are a fixed
  // ROW_H tall, so the window is pure arithmetic off scrollTop/clientHeight. ---
  const [range, setRange] = useState({ start: 0, end: MIN_WINDOW });
  const recomputeRange = useCallback((): void => {
    const el = listRef.current;
    if (!el) return;
    const first = Math.max(0, Math.floor(el.scrollTop / ROW_H) - OVERSCAN);
    const count = Math.max(MIN_WINDOW, Math.ceil(el.clientHeight / ROW_H) + OVERSCAN * 2);
    setRange((prev) =>
      prev.start === first && prev.end === first + count ? prev : { start: first, end: first + count },
    );
  }, []);
  const scrollToIndex = useCallback((index: number): void => {
    const el = listRef.current;
    if (!el) return;
    const y = index * ROW_H;
    if (y < el.scrollTop) el.scrollTop = y;
    else if (y + ROW_H > el.scrollTop + el.clientHeight) el.scrollTop = y - el.clientHeight + ROW_H;
  }, []);

  // Branch list for the filter, refreshed per repo.
  useEffect(() => {
    if (!root || !git) {
      setBranches(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const b = await git.branches(root);
        if (!cancelled) setBranches(b);
      } catch {
        if (!cancelled) setBranches(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [root, git, refreshToken]);

  // Page 0 for the current repo + filter; supersedes any in-flight run.
  useEffect(() => {
    // A fresh page 0 resets the auto-pagination budget and loaded count, and
    // starts a new generation so any in-flight loadMore drops its stale page.
    autoStreak.current = 0;
    commitsLenRef.current = 0;
    genRef.current += 1;
    setAutoPaused(false);
    if (!root || !git) {
      setCommits([]);
      setLastFull(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const page = await git.log(root, { rev, max: PAGE, skip: 0 });
        if (cancelled) return;
        setCommits(page);
        commitsLenRef.current = page.length;
        setLastFull(page.length === PAGE);
      } catch (e) {
        if (cancelled) return;
        setCommits([]);
        setLastFull(false);
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [root, git, rev, refreshToken]);

  // The loaded-commit count lives in a ref managed only by the fetch paths (not
  // render-time), so the next skip is always distinct even before React commits
  // the appended page; `loadingRef` rejects overlapping loads synchronously
  // (state lags a render).
  const loadingRef = useRef(false);
  const commitsLenRef = useRef(0);
  // Bumped whenever page 0 reloads (repo/filter/refresh). A `loadMore` in flight
  // across a reload compares its captured generation and drops its stale page so
  // it can never append onto a freshly-reloaded list.
  const genRef = useRef(0);
  const loadMore = useCallback(
    async (auto: boolean): Promise<void> => {
      if (!root || !git || loadingRef.current) return;
      loadingRef.current = true;
      setLoadingMore(true);
      const baseRev = rev;
      const baseLen = commitsLenRef.current;
      const gen = genRef.current;
      try {
        const page = await git.log(root, { rev: baseRev, max: PAGE, skip: baseLen });
        // Drop the result if the repo/filter changed or page 0 reloaded meanwhile.
        if (rootRef.current !== root || revRef.current !== baseRev || genRef.current !== gen) return;
        commitsLenRef.current = baseLen + page.length;
        setCommits((prev) => [...prev, ...page]);
        setLastFull(page.length === PAGE);
        // Each load is a distinct page (loadingRef serializes), so count one
        // toward the auto budget here and pause once the budget is spent.
        if (auto) {
          autoStreak.current += 1;
          if (autoStreak.current >= 5) setAutoPaused(true);
        }
      } catch (e) {
        if (rootRef.current === root && revRef.current === baseRev) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        loadingRef.current = false;
        setLoadingMore(false);
      }
    },
    [root, git, rev],
  );

  // rAF-throttled vertical infinite scroll: auto-fetch the next page as the list
  // nears the bottom, gating on synchronous refs (state lags a render, which
  // would let extra pages slip past the 5-page budget). Fires from the wheel
  // handler's manual scrollTop writes and from native scrollbar use.
  const onListScroll = useCallback((): void => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      const el = listRef.current;
      if (!el) return;
      recomputeRange();
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 220;
      if (nearBottom && lastFull && !loadingRef.current && autoStreak.current < 5) {
        void loadMore(true);
      }
    });
  }, [lastFull, loadMore, recomputeRange]);

  // Mutations refresh the whole client via onMutated (which bumps refreshToken
  // back into the load effects); errors surface inline like the Changes tab.
  const runMutation = useCallback(
    async (op: () => Promise<unknown>): Promise<void> => {
      if (busy) return;
      setBusy(true);
      try {
        await op();
        onMutated();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, onMutated],
  );

  const submitPrompt = useCallback(
    (name: string): void => {
      const active = prompt;
      setPrompt(null);
      if (!root || !git || !active) return;
      if (active.kind === 'branch') {
        void runMutation(() =>
          git.createBranch(root, name, { startPoint: active.commit.hash, checkout: true }),
        );
      } else {
        void runMutation(() => git.createTag(root, name, { rev: active.commit.hash }));
      }
    },
    [prompt, root, git, runMutation],
  );

  const openCommitMenu = useCallback(
    (e: MouseEvent, commit: GitCommit): void => {
      e.preventDefault();
      e.stopPropagation();
      if (!root || !git) return;
      const items: ContextMenuEntry[] = [
        {
          id: 'checkout-commit',
          label: 'Checkout commit (detached)',
          icon: GitBranch,
          onSelect: () =>
            void runMutation(() => git.checkoutCommit(root, commit.hash, { detach: true })),
        },
        {
          id: 'create-branch',
          label: 'Create branch here…',
          icon: GitBranchPlus,
          onSelect: () => setPrompt({ kind: 'branch', commit }),
        },
        {
          id: 'create-tag',
          label: 'Create tag here…',
          icon: Tag,
          onSelect: () => setPrompt({ kind: 'tag', commit }),
        },
        { type: 'separator', id: 'sep' },
        {
          id: 'isolate',
          label: isolated === commit.hash ? 'Clear isolation' : 'Isolate history to here',
          icon: Focus,
          onSelect: () => setIsolated(isolated === commit.hash ? null : commit.hash),
        },
        {
          id: 'copy-hash',
          label: 'Copy commit hash',
          icon: Copy,
          onSelect: () => void navigator.clipboard?.writeText(commit.hash),
        },
      ];
      openContextMenu(e.clientX, e.clientY, items);
    },
    [root, git, runMutation, openContextMenu, isolated],
  );

  // Right-clicking a branch/tag PILL acts on that ref (checkout the branch, or a
  // detached checkout of a tag), not on the commit; it stops the event so the
  // commit menu does not also open.
  const openRefMenu = useCallback(
    (e: MouseEvent, pill: RefPill): void => {
      e.preventDefault();
      e.stopPropagation();
      if (!root || !git) return;
      const items: ContextMenuEntry[] = [];
      if (pill.kind === 'tag') {
        items.push({
          id: 'checkout',
          label: `Checkout ${pill.label} (detached)`,
          icon: GitBranch,
          onSelect: () =>
            void runMutation(() => git.checkoutCommit(root, pill.label, { detach: true })),
        });
      } else if (pill.label !== 'HEAD') {
        items.push({
          id: 'checkout',
          label: `Checkout ${pill.label}`,
          icon: GitBranch,
          onSelect: () => void runMutation(() => git.checkout(root, pill.label)),
        });
      }
      items.push({
        id: 'copy',
        label: 'Copy name',
        icon: Copy,
        onSelect: () => void navigator.clipboard?.writeText(pill.label),
      });
      openContextMenu(e.clientX, e.clientY, items);
    },
    [root, git, runMutation, openContextMenu],
  );

  // Lay out lanes over the FULL list so colors stay correct, then visually
  // filter the rendered rows by the header search query (the lane geometry of a
  // visible row is unchanged whether or not its neighbors are hidden).
  const rows = useMemo(() => layoutCommits(commits), [commits]);
  // The isolate set: the isolated commit plus every ancestor within the loaded
  // window. Rows outside it are dimmed. null when no isolate is active.
  const isolateSet = useMemo(
    () => (isolated ? ancestorsOf(commits, isolated) : null),
    [commits, isolated],
  );
  const query = search.trim().toLowerCase();
  const visibleRows = useMemo(
    () => (query ? rows.filter((r) => commitMatches(r.commit, query)) : rows),
    [rows, query],
  );
  const graphWidth = useMemo(
    () => RAIL_PAD * 2 + Math.max(1, ...rows.map((r) => r.width)) * LANE_W,
    [rows],
  );

  // The uncommitted node (when dirty) is item 0; commits follow. Windowing works
  // over this combined index space.
  const offset = workingDirty ? 1 : 0;
  const totalItems = offset + visibleRows.length;
  // Recompute the window when the item count changes (a page load, a filter) and
  // whenever the viewport resizes.
  useEffect(() => {
    recomputeRange();
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recomputeRange());
    ro.observe(el);
    return () => ro.disconnect();
  }, [totalItems, recomputeRange]);

  // Reveal a commit: select it and scroll its row into view, remembering it as a
  // pending target if it is not loaded yet. Keyed on the token so the same hash
  // can be revealed repeatedly.
  useEffect(() => {
    if (!revealHash || !revealToken) return;
    onSelect({ kind: 'commit', hash: revealHash });
    pendingReveal.current = revealHash;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealToken]);

  // Drive a pending reveal: if the target is loaded, scroll to it and clear;
  // otherwise fetch the next page (bypassing the auto-pause) and try again when
  // it appends. Give up once the history is exhausted.
  useEffect(() => {
    const hash = pendingReveal.current;
    if (!hash) return;
    const i = visibleRows.findIndex((r) => r.commit.hash === hash);
    if (i >= 0) {
      pendingReveal.current = null;
      scrollToIndex(i + offset);
      return;
    }
    if (lastFull && !loadingRef.current) void loadMore(false);
    else if (!lastFull) pendingReveal.current = null;
  }, [visibleRows, lastFull, offset, scrollToIndex, loadMore, revealToken]);

  if (!root) {
    return (
      <div className="mp-git__empty">
        <GitBranch size={24} aria-hidden className="mp-git__empty-icon" />
        <p className="mp-git__empty-title">No Git repository</p>
        <p className="mp-git__empty-note">No Git repository for the active file or workspace.</p>
      </div>
    );
  }

  const currentBranch = branches?.current ?? null;

  // The windowed slice of items to actually render (plus spacers for the rest).
  const windowStart = Math.min(range.start, totalItems);
  const windowEnd = Math.min(range.end, totalItems);
  const renderItem = (index: number): ReactElement | null => {
    if (offset && index === 0) {
      return (
        <div
          key="working"
          className="mp-git-graph__row mp-git-graph__uncommitted"
          role="option"
          aria-selected={selection === 'working'}
          data-selected={selection === 'working' || undefined}
          style={{ '--mp-git-dotx': `${LANE_W / 2}px` } as CSSProperties}
          tabIndex={0}
          onClick={() => onSelect('working')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect('working');
            }
          }}
        >
          <div className="mp-git-graph__sticky">
            <span className="mp-git-graph__reftag" />
            <span className="mp-git-graph__rail-cell">
              <span className="mp-git-graph__node mp-git-graph__node--working">
                <span className="mp-git-graph__working-dot" aria-hidden />
              </span>
            </span>
          </div>
          <span className="mp-git-graph__msg">
            <span className="mp-git-graph__subject mp-git-graph__subject--working">
              Uncommitted changes
            </span>
          </span>
        </div>
      );
    }
    const row = visibleRows[index - offset];
    if (!row) return null;
    return (
      <GraphRowView
        key={row.commit.hash}
        row={row}
        currentBranch={currentBranch}
        selected={isCommitSelected(selection, row.commit.hash)}
        dimmed={isolateSet != null && !isolateSet.has(row.commit.hash)}
        onSelect={onSelect}
        onContextMenu={openCommitMenu}
        onPillContextMenu={openRefMenu}
      />
    );
  };

  return (
    <div
      className="mp-git-graph"
      data-lane-collapsed={collapsed || undefined}
      style={
        {
          '--mp-git-graph-w-full': `${graphWidth}px`,
          '--mp-git-collapse': collapsed ? 1 : 0,
          '--mp-git-branch-collapse': collapsed ? 1 : 0,
        } as CSSProperties
      }
    >
      <div className="mp-git-graph__toolbar">
        <button
          type="button"
          className="mp-iconbtn mp-git-graph__collapse-toggle"
          aria-label={collapsed ? 'Expand the graph' : 'Collapse the graph'}
          aria-pressed={collapsed}
          title={collapsed ? 'Expand the graph (branch + lanes)' : 'Collapse the graph (branch + lanes)'}
          onClick={toggleCollapsed}
        >
          {collapsed ? (
            <ChevronsLeftRight size={14} aria-hidden />
          ) : (
            <ChevronsRightLeft size={14} aria-hidden />
          )}
        </button>
        <GitBranchCombobox branches={branches} value={rev} onChange={setRev} />
        <button
          type="button"
          className="mp-iconbtn mp-git-graph__refresh"
          aria-label="Refresh history"
          title="Refresh history"
          onClick={onMutated}
        >
          <RefreshCw size={14} aria-hidden />
        </button>
        {isolated && (
          <button
            type="button"
            className="mp-git-graph__isolate-chip"
            aria-label="Clear isolation"
            title="Clear isolation"
            onClick={() => setIsolated(null)}
          >
            <Focus size={12} aria-hidden />
            Isolating
            <X size={12} aria-hidden />
          </button>
        )}
      </div>

      <div className="mp-git-graph__head" role="presentation">
        <span className="mp-git-graph__head-cell">Branch / Tag</span>
        <span className="mp-git-graph__head-cell">Graph</span>
        <span className="mp-git-graph__head-cell">Commit Message</span>
      </div>

      {error && (
        <p className="mp-git__error" role="alert">
          {error}
        </p>
      )}

      {prompt && (
        <GitNamePrompt
          title={
            prompt.kind === 'branch'
              ? `Create branch from ${prompt.commit.short}`
              : `Create tag at ${prompt.commit.short}`
          }
          placeholder={prompt.kind === 'branch' ? 'Branch name' : 'Tag name'}
          onSubmit={submitPrompt}
          onCancel={() => setPrompt(null)}
        />
      )}

      <div
        ref={listRef}
        className="mp-git-graph__list"
        role="listbox"
        aria-label="Commit history"
        tabIndex={0}
        onScroll={onListScroll}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          if (visibleRows.length === 0) return;
          e.preventDefault();
          const cur =
            selection && selection !== 'working'
              ? visibleRows.findIndex((r) => r.commit.hash === selection.hash)
              : -1;
          const last = visibleRows.length - 1;
          // Cmd/Ctrl jumps to the very top / bottom of the loaded history.
          const idx =
            e.metaKey || e.ctrlKey
              ? e.key === 'ArrowDown'
                ? last
                : 0
              : e.key === 'ArrowDown'
                ? cur < 0
                  ? 0
                  : Math.min(cur + 1, last)
                : cur <= 0
                  ? 0
                  : cur - 1;
          onSelect({ kind: 'commit', hash: visibleRows[idx].commit.hash });
          scrollToIndex(idx + offset);
          listRef.current?.focus({ preventScroll: true });
        }}
      >
        <div className="mp-git-graph__list-inner">
          {windowStart > 0 && <div style={{ height: windowStart * ROW_H }} aria-hidden />}
          {Array.from({ length: Math.max(0, windowEnd - windowStart) }, (_, k) =>
            renderItem(windowStart + k),
          )}
          {totalItems - windowEnd > 0 && (
            <div style={{ height: (totalItems - windowEnd) * ROW_H }} aria-hidden />
          )}
        </div>
      </div>

      {!error && rows.length === 0 && !loading && <p className="mp-git__clean">No commits.</p>}

      {!error && query && visibleRows.length === 0 && rows.length > 0 && (
        <p className="mp-git__clean">No matching commits.</p>
      )}

      {lastFull && autoPaused && (
        <button
          type="button"
          className="mp-git-graph__more"
          disabled={loadingMore}
          onClick={() => {
            setAutoPaused(false);
            autoStreak.current = 0;
            void loadMore(false);
          }}
        >
          {loadingMore ? 'Loading' : 'Load more'}
        </button>
      )}
    </div>
  );
}
